/**
 * Combatant fixtures — the parameterised fighters the harness pits against
 * each other (M1-07).
 *
 * These are **balance instruments, not characters**. A spearman here is a
 * defined point in the space of "what a trained man with reach and a leather
 * jerkin is worth", chosen so that the envelope tests measure something
 * meaningful when the wound tables move. M2-05 is where creatures get real
 * behaviour and M2-04 is where the era kit gets real depth; what these have to
 * be is *stable*, because a golden-seed snapshot is only a regression test if
 * the fixture under it stops changing.
 *
 * The set is chosen to span the questions M1-07 asks:
 *
 * - **spearman vs spearman** — the even fight, and the length envelope.
 * - **farmer vs wolf** — can an ordinary person survive the thing in the wood?
 *   (No, mostly.)
 * - **two farmers vs wolf** — *cooperation must be worth more than gear*, the
 *   anchor the whole milestone is pointed at.
 * - **axeman vs unarmoured farmer** — what gear and skill are worth, so the
 *   cooperation anchor has something to be compared against.
 */
import { createAttributes, PRONOUNS } from '@annwn/sim';
import { BOAR_HIDE, BYRNIE, GREAVES, HELM, JERKIN, STAG_HIDE, TUNIC, YSGITHYRWYN_HIDE, } from './armour.js';
import { attackProfile } from './attackProfiles.js';
import { BOAR_PLAN, HUMAN_PLAN, STAG_PLAN, WOLF_PLAN, YSGITHYRWYN_PLAN, } from './bodyPlans/index.js';
import { AXE, KNIFE, SPEAR, STAFF, STAG_ANTLERS, WOLF_JAWS, BOAR_TUSKS, YSGITHYRWYN_TUSKS, } from './weapons.js';
/**
 * A spearman of a llys war-band: trained, reach, and enough leather to matter.
 * The reference fighter — every envelope is stated relative to this one.
 */
export const SPEARMAN = {
    id: 'spearman',
    name: 'the spearman',
    pronouns: 'he',
    plan: HUMAN_PLAN,
    attributes: createAttributes({ frame: 12, agility: 12, toughness: 12 }),
    weapon: SPEAR,
    armour: [TUNIC, JERKIN],
    skill: 2,
};
/** An axeman: more damage, less reach, and the same training. */
export const AXEMAN = {
    id: 'axeman',
    name: 'the axeman',
    pronouns: 'he',
    plan: HUMAN_PLAN,
    attributes: createAttributes({ frame: 14, agility: 10, toughness: 12 }),
    weapon: AXE,
    armour: [TUNIC, JERKIN, HELM],
    skill: 2,
};
/** A knifeman: fast, unarmoured, and dangerous only if he gets inside. */
export const KNIFEMAN = {
    id: 'knifeman',
    name: 'the knifeman',
    pronouns: 'she',
    plan: HUMAN_PLAN,
    attributes: createAttributes({ frame: 9, agility: 14, toughness: 10 }),
    weapon: KNIFE,
    armour: [TUNIC],
    skill: 2,
};
/** A teulu-man in mail. What wealth buys, and the control on the gear question. */
export const NOBLE = {
    id: 'noble',
    name: 'the teulu-man',
    pronouns: 'he',
    plan: HUMAN_PLAN,
    attributes: createAttributes({ frame: 13, agility: 12, toughness: 12 }),
    weapon: AXE,
    armour: [TUNIC, BYRNIE, HELM, GREAVES],
    skill: 3,
};
/**
 * A farmer with a staff. Untrained, unarmoured, and the person most of Annwn
 * actually is — which is why the cooperation anchor is written about them.
 */
export const FARMER = {
    id: 'farmer',
    name: 'the farmer',
    pronouns: 'they',
    plan: HUMAN_PLAN,
    attributes: createAttributes({ frame: 11, agility: 9, toughness: 11 }),
    weapon: STAFF,
    armour: [TUNIC],
    skill: 0,
};
/** A wolf. A stub, honestly — M2-05 owns how one actually hunts. */
export const WOLF = {
    id: 'wolf',
    name: 'the wolf',
    pronouns: 'it',
    plan: WOLF_PLAN,
    attributes: createAttributes({ frame: 10, agility: 13, toughness: 8 }),
    weapon: WOLF_JAWS,
    armour: [],
    skill: 1,
};
/**
 * A boar. Half a ton, no sense at all, and a hide that makes cutting at its
 * body close to pointless — which is the harness's way of asking whether aimed
 * blows are worth their penalty (M2-05).
 */
export const BOAR = {
    id: 'boar',
    name: 'the boar',
    pronouns: 'it',
    plan: BOAR_PLAN,
    attributes: createAttributes({ frame: 15, agility: 9, toughness: 14 }),
    weapon: BOAR_TUSKS,
    armour: [BOAR_HIDE],
    skill: 0,
};
/**
 * Ysgithyrwyn, White-Tusk, Chief of Boars (M5-04) — the MVP's boss, as a
 * balance instrument.
 *
 * The one fixture that exists to be *asked a question of*: the README's
 * envelope says a prepared party of four beats him three times in five and a
 * pair does not, and this is the point in stat-space that has to make that true.
 * Nothing about him is a mechanism — he is a boar's archetype with the frame of
 * a cart-horse, a hide worth three, and tusks that arrive a level above any
 * blade — so if the envelope ever drifts, the fix is one of these numbers and
 * not a line of code. That is the thesis of the whole hunt, stated as a fixture:
 * a boss is an ordinary body with the numbers turned up.
 *
 * Frame 20 is the ceiling of the strength curve (`ST+3`) and toughness 20 the
 * ceiling of the wound curve (`TO 5`), so every blow that is not aimed at a bare
 * place is very nearly shrugged — which is what makes the aimed blow, and the
 * fourth hunter who buys the room to throw it, worth what they cost.
 */
export const YSGITHYRWYN = {
    id: 'ysgithyrwyn',
    name: 'Ysgithyrwyn',
    pronouns: 'he',
    plan: YSGITHYRWYN_PLAN,
    // Slow to turn (agility 8) is the whole opening: he commits to a stroke and a
    // party with reach and discipline steps off the line of it. Take that away and
    // he is unbeatable; it is as load-bearing as the toughness.
    attributes: createAttributes({ frame: 20, agility: 8, toughness: 20 }),
    weapon: YSGITHYRWYN_TUSKS,
    armour: [YSGITHYRWYN_HIDE],
    // A lifetime of killing the men who came before. Not trained — practised, which
    // in this engine is the same number and a different word.
    skill: 2,
};
/** A stag. In the harness it is a control: fast, fragile, and rarely caught. */
export const STAG = {
    id: 'stag',
    name: 'the stag',
    pronouns: 'it',
    plan: STAG_PLAN,
    attributes: createAttributes({ frame: 13, agility: 14, toughness: 10 }),
    weapon: STAG_ANTLERS,
    armour: [STAG_HIDE],
    skill: 0,
};
const ARCHETYPES = [
    SPEARMAN,
    AXEMAN,
    KNIFEMAN,
    NOBLE,
    FARMER,
    WOLF,
    BOAR,
    YSGITHYRWYN,
    STAG,
];
export const ARCHETYPE_KIT = new Map(ARCHETYPES.map((one) => [one.id, one]));
export function archetype(id) {
    const found = ARCHETYPE_KIT.get(id);
    if (!found) {
        throw new Error(`unknown archetype '${id}' (have: ${[...ARCHETYPE_KIT.keys()].join(', ')})`);
    }
    return found;
}
/**
 * Turn an archetype into a fighter's kit for one fight.
 *
 * The `index` makes ids unique when a side fields two of the same thing — two
 * farmers must be `farmer-0` and `farmer-1`, because the fight orders actions
 * by id and a tie there would be a tie a replay could break either way.
 */
export function kitFor(one, side, index = 0) {
    return {
        id: `${one.id}-${side}${index}`,
        name: index === 0 ? one.name : `${one.name} (${index + 1})`,
        pronouns: PRONOUNS[one.pronouns],
        plan: one.plan,
        attributes: one.attributes,
        weapon: one.weapon,
        profile: attackProfile(one.weapon.profileId),
        armour: one.armour,
        skill: one.skill,
        side,
    };
}
/** A side of `count` identical fighters. The cooperation anchor's other half. */
export function sideOf(one, side, count = 1) {
    return Array.from({ length: count }, (_, index) => kitFor(one, side, index));
}
//# sourceMappingURL=archetypes.js.map